import { LoadingState, VideoStatus, VideoTag } from '@/interfaces'
import type { Folder } from '@/services/folder/types'
import type { VideoCategory } from '@/services/types'
import type { Language } from '@/services/general/types'

export interface Video {
  video_id: string
  organization_name: string
  organization_id: number
  folder_id: number
  title: string
  description: string
  upload_date: string
  last_modified: string
  duration_in_ms: number
  status: string
  pricing_model: string
  language_id: number
  private?: boolean
  formats: Format[]
  thumb_ext: string
  public_url: string
  thumbnails: Thumbnail[] | string[]
  id?: string
  guid?: string
  links?: string
  clickthrough_urls?: ClickthroughUrl[]
  ownership?: boolean
  tags?: Array<{ id?: number; name: string }>
  language_2_letters?: string
  categories?: number[]
  thumbnail_url?: string | string[]
  localId?: string
  thumbnailsLoading?: boolean
  organization_profile_image?: string
  countries_allow?: string[] | undefined | null
  countries_disallow?: string[] | undefined | null
  is_vertical: boolean
  handle?: string
  icon_image_url?: string
  country_allow?: string[]
  country_disallow?: string[]
  shares?: number
  likes?: number
  // views?: number
  source_type?: string
  is_paired_to_page?: boolean
  ai_generated_article_url?: string
}

export type VideoDraftItem = Partial<Video> & {
  video_id: string
  is_vertical: boolean
  thumbnails?: Thumbnail[] | string[]
  title: string
  duration_in_ms: number
}
export interface Videos {
  videos: Video[]
  total_videos: number
  results_per_page: number
}

export interface ClickthroughUrl {
  id?: number | string
  url?: string
  start_time?: number
  end_time?: number
  readonly?: boolean
  valid?: boolean
  overwrite?: boolean
  isInUse?: boolean
}

export interface PairedUrl {
  id: number | string
  url: string
  readonly?: boolean
  valid?: boolean
  overwrite?: boolean
  isInUse?: boolean
  isNotApproved?: boolean
}

export type DisplayPairedUrl = PairedUrl & {
  isAiUrl?: boolean
}

export interface Format {
  codecs_RFC6381: string
  size: number
  format: string
  width: number
  height: number
  label: string
  path_in_cdn?: string
  path_in_s3?: string
}

export interface Thumbnail {
  name: string
  url: string
}

export interface VideoUpdate {
  video_id: string
  title: string
  description: string
  private?: boolean
  language_id?: number
  pricing_model: string
  folder_id: number
  categories?: number[]
  guid?: string
  clickthrough_urls?: ClickthroughUrl[]
  thumbnail_url?: string
  countries_allow?: string[]
  countries_disallow?: string[]
  tags?: VideoTag[]
}

export interface VideoFilters {
  min_duration?: number
  max_duration?: number
  categories_id?: number[]
  categories?: number | number[]
  search?: string
  languageId?: number
  languages?: number | number[]
  sort_by?: string
  sort_order?: string
  pricing_model?: string
  organization_id?: number
  folder_id?: number
  results_per_page?: number
  page_number?: number
  private?: boolean
  show_packages?: boolean
  show_public?: boolean
  is_vertical?: boolean
  video_settings?: boolean
}

export interface UploadVideoRequest {
  id: string
  signed_url: string
  public_url: string
}

export interface UploadVideo extends PostVideo {
  file: File
  id: string
  uploadProgress: number
  loadingState: LoadingState
  localId: string
  status: VideoStatus
  request?: UploadVideoRequest
  exposure: string
  folder: number | null
  controller?: AbortController
  thumbnailsLoading: boolean
  clickthroughUrls: string
  hasCountryRestrictions?: boolean
  countryRestrictionType?: string
  tags?: Array<{ name: string; id?: number }>
  thumbnail_url?: string
  thumbnails: string[]
  is_vertical: boolean
  isSelected?: boolean
  isSettingsUpdated?: boolean
  failMessage?: string
  draftId: number | null,
  duration_in_ms: number | null
}

export interface PostVideo {
  source_url: string
  title: string
  description?: string
  private?: boolean
  language_id?: number
  pricing_model?: string
  folder_id?: number
  categories?: number[]
  guid?: string
  clickthrough_urls?: ClickthroughUrl[]
  thumbnail_url?: string
  tags?: Array<{ name: string; id?: number }>
  source_type?: 'mrss'
  source_id?: number
  countries_allow: string[] // country codes array
  countries_disallow: string[] // country codes array
}

export interface Stream {
  organization_id: number
  id: number | string
  title: string
  url: string
  last_run: string
  is_active: boolean
  folder: Folder
  category: VideoCategory
  language: Language
  type: string
  is_public: boolean
  pricing_model: string
  approved: boolean
  created_date: string
  tags?: Array<{ name: string; id?: number }>
  last_run_feed_status_valid: boolean
  last_run_number_of_errors: number
  countries_allow?: string[] | null // country codes array
  countries_disallow?: string[] | null // country codes array
}

// for post/put
export interface PostStream extends Partial<Stream> {
  title: string
  youtube_channel_id?: string
  youtube_playlist_id?: string
  mrss_feed_url?: string
  category_id: number | null
  is_public: boolean
  folder_id?: number | null
  pricing_model?: string // - rev_share - free
  language_id?: number
  type?: string
  is_active?: boolean
  tags?: VideoTag[]
  countries_allow?: string[] | null // Array of country codes (ISO 3166-1 alpha-2)
  countries_disallow?: string[] | null // Array of country codes (ISO 3166-1 alpha-2)
}

export interface Streams {
  streams: Stream[]
  total: number
}

export interface VideoIn {
  video: Video
}

export interface StreamsFolders {
  folders: Folder[]
}

export interface ProcessingVideo {
  video_id: string
  title?: string
  thumbnail_url?: string | null
  status: ProcessingVideoStatus
  error?: string
  userId?: number
}

export enum ProcessingVideoStatus {
  Transcoding = 'transcoding',
  Pending = 'pending',
  Active = 'active',
  Failed = 'failed',
  Fetching = 'fetching',
  Processing = 'PROCESSING',
  Completed = 'COMPLETED'
}

export enum VideoPollingSource {
  VideoUpload = 'video_upload',
  AiGenerateVideo = 'ai_generate_video',
}

